import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "../../../../components/Form/TextField";
import Select from "../../../../components/Form/Select";
import Chip from "@mui/material/Chip";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import CardContainer from "../../../../components/Card/CardContainer";
import SectionHeader from "./SectionHeader";
import { BUSINESS_OFFERING } from "../../../../utils/constants";
import {
  SECTION_OFFER_TITLE,
  SECTION_OFFER_SUBTITLE,
  SECTION_OFFER_TYPE_LABEL,
  SECTION_OFFER_DEFAULT_VALUE,
  SECTION_OFFER_BUSINESS_VALUE_PROPOSITION_LABEL,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import { BRAND_EDIT_MULTILINE_ROWS } from "../utils/constants";

export const OfferSection = ({ formMethods, hasError = false }) => {
  const { t } = useTranslationApp();
  const [isEditing, setIsEditing] = useState(false);
  const [editedOffer, setEditedOffer] = useState("");
  const [editedStyle, setEditedStyle] = useState("");

  const businessOffering = formMethods?.watch?.("businessOffering") || "";
  const businessValueProposition =
    formMethods?.watch?.("businessValueProposition") || "";

  useEffect(() => {
    if (!isEditing && formMethods?.control) {
      setEditedOffer(businessOffering);
      setEditedStyle(businessValueProposition);
    }
  }, [
    businessOffering,
    businessValueProposition,
    isEditing,
    formMethods?.control,
  ]);

  useEffect(() => {
    if (formMethods?.control) {
      setEditedOffer(businessOffering);
      setEditedStyle(businessValueProposition);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formMethods?.control]);

  if (!formMethods || !formMethods.control) {
    return null;
  }

  const { control, errors, validationRules } = formMethods;

  const getOfferLabel = () => {
    if (!businessOffering) {
      return t(BUSINESS_OFFERING[0]?.label || SECTION_OFFER_DEFAULT_VALUE);
    }
    const selectedOption = BUSINESS_OFFERING.find(
      (option) => option.value === businessOffering
    );
    return selectedOption ? t(selectedOption.label) : businessOffering;
  };

  const onSave = () => {
    if (!formMethods?.setValue) return;
    formMethods.setValue("businessOffering", editedOffer);
    formMethods.setValue("businessValueProposition", editedStyle);
    setIsEditing(false);
  };

  const onEdit = async () => {
    setIsEditing(true);
    // Trigger validation when entering edit mode
    await formMethods.trigger?.([
      "businessOffering",
      "businessValueProposition",
    ]);
  };

  return (
    <CardContainer title="" hasError={hasError}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          minHeight: "100%",
        }}
      >
        {/* Header */}
        <SectionHeader
          icon={LocalOfferIcon}
          title={SECTION_OFFER_TITLE}
          subtitle={SECTION_OFFER_SUBTITLE}
          isEditing={isEditing}
          onEdit={onEdit}
          onSave={onSave}
        />

        {/* Content */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              display: isEditing ? "flex" : "none",
              flexDirection: "column",
              gap: 2,
              flex: 1,
            }}
          >
            {/* Offer Type Select */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: "text.primary",
                  fontSize: "14px",
                  display: "block",
                  mb: 1,
                }}
              >
                {t(SECTION_OFFER_TYPE_LABEL)}
              </Typography>
              <Select
                control={control}
                name="businessOffering"
                value={editedOffer}
                actionsChange={(value) => setEditedOffer(value)}
                rules={validationRules.businessOffering}
                errors={errors}
                options={BUSINESS_OFFERING}
                doTranslate
                fullWidth
                variant="filled"
              />
            </Box>

            {/* Visual Style Textarea */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <Typography
                variant="body2"
                sx={{
                  color: "text.primary",
                  fontSize: "14px",
                  display: "block",
                  mb: 1,
                }}
              >
                {t(SECTION_OFFER_BUSINESS_VALUE_PROPOSITION_LABEL)}
              </Typography>
              <TextField
                control={control}
                variant="filled"
                name="businessValueProposition"
                value={editedStyle}
                actionsChange={(value) => setEditedStyle(value)}
                rules={validationRules.businessValueProposition}
                errors={errors}
                multiline
                minRows={BRAND_EDIT_MULTILINE_ROWS}
                maxRows={BRAND_EDIT_MULTILINE_ROWS}
                fullWidth
              />
            </Box>
          </Box>

          {!isEditing && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}
            >
              {/* Offer Type Chip */}
              <Chip
                label={getOfferLabel()}
                sx={{
                  alignSelf: "flex-start",
                  backgroundColor: "#F5F5F5",
                  color: "text.primary",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  px: 1.5,
                  py: 0.75,
                  height: "auto",
                }}
              />

              {/* Style Description */}
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  lineHeight: 1.6,
                  fontSize: "0.875rem",
                  flex: 1,
                }}
              >
                {businessValueProposition}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </CardContainer>
  );
};

export default OfferSection;
